/**
 * Rendu des vehicules: la voiture et la barque.
 *
 * Trois partis pris:
 *
 * 1. Geometrie fusionnee. Chaque vehicule est un petit nombre de maillages
 *    (carrosserie, un essieu avant, un essieu arriere, les phares — coque, deux
 *    rames, l'ancre, le sillage), chacun fabrique en une seule geometrie par
 *    `fusionner`, avec les couleurs portees par les sommets. Une voiture coute
 *    donc 4 appels de dessin, une barque 3 a 5 selon l'ancre et le sillage,
 *    quelle que soit la richesse du modele.
 *
 * 2. Les parties mobiles determinent le decoupage. Les deux roues d'un meme
 *    essieu partagent leur axe: fusionnees, une seule rotation autour de x les
 *    fait tourner toutes les deux correctement. Les rames, elles, doivent
 *    balayer symetriquement: elles restent donc separees.
 *
 * 3. Aucune lumiere ajoutee. Les phares s'allument par leur `emissive`, pas par
 *    une PointLight: le nombre de lumieres de la scene fait partie de la clef de
 *    cache des programmes de three.js, en ajouter une par vehicule ferait
 *    recompiler tous les materiaux a chaque pose (voir la note de
 *    client/render/batiments.ts, POOL_LUMIERES).
 *
 * Liberation: tout ce qui est cree ici est detruit dans `libererMaillage`, qui
 * parcourt l'arbre au lieu de se fier a une liste tenue a la main. C'est la fuite
 * qu'on a payee dans le rendu des batiments (fantome reconstruit a chaque
 * rotation): elle ne doit pas revenir.
 */
import * as THREE from 'three'
import type { Vehicule, VehiculeKind } from '../../shared/types/monde.ts'
import type { Vec2 } from '../../shared/types/world.ts'
import { clamp, melangerCouleur, smoothstep } from '../../shared/sim/alea.ts'
import { vehiculeDef } from '../../shared/data/vehicules.ts'
import { fusionner, tr, type Partie } from './geometrie.ts'
import { journal } from '../log/journal.ts'

// ------------------------------------------------------------------ reglages

/** Rayon d'une roue, en metres. Sert aussi a la vitesse de rotation. */
const RAYON_ROUE = 0.33

/** Position des essieux sur z, en metres. */
const Z_ESSIEU_AVANT = 1.35
const Z_ESSIEU_ARRIERE = -1.35

/** Ecartement des roues, en metres. */
const X_ROUE = 0.92

/** Couleur des phares eteints (le jour) et allumes (la nuit). */
const PHARE_ETEINT = 0x1a1710
const PHARE_ALLUME = 0xfff0c8

/** Amplitude du balayage des rames, en radians. */
const AMPLITUDE_RAME = 0.5

/** Angle de repos des rames, trainant vers l'arriere, en radians. */
const REPOS_RAME = 0.5

/** Inclinaison qui met la pelle dans l'eau, en radians. */
const PLONGEE_RAME = 0.24

/** Vitesse au-dela de laquelle le sillage est a son maximum, en m/s. */
const VITESSE_SILLAGE = 2.5

/** Lissage de la vitesse mesuree, en secondes. */
const LISSAGE_VITESSE = 0.18

// ------------------------------------------------------------------ maillage

interface MaillageVehicule {
  groupe: THREE.Group
  /** Essieux de la voiture, dans l'ordre avant puis arriere. */
  essieux: THREE.Mesh[]
  /** Rames de la barque, avec leur cote (-1 a babord, +1 a tribord). */
  rames: { maillage: THREE.Mesh; cote: number }[]
  /** Phares avant: leur `emissive` s'allume la nuit. */
  phares: THREE.Mesh | null
  /** Ancre, visible seulement quand elle est jetee. */
  ancre: THREE.Mesh | null
  /** Sillage, sous la coque, opacite proportionnelle a la vitesse. */
  sillage: THREE.Mesh | null
  matSillage: THREE.MeshBasicMaterial | null
  matPhares: THREE.MeshLambertMaterial | null
  /** Materiaux a teinter pour le fantome. */
  materiaux: THREE.Material[]
}

/**
 * Libere geometries et materiaux d'un maillage. Rien n'est mis en cache ici: il
 * n'y a jamais qu'une poignee de vehicules dans le monde, et un cache partage
 * obligerait a distinguer ce qu'on a le droit de detruire — precisement le piege
 * ou le rendu des batiments a fui.
 */
function libererMaillage(m: MaillageVehicule): void {
  m.groupe.traverse((o) => {
    const maille = o as THREE.Mesh
    if (!maille.isMesh) return
    maille.geometry?.dispose()
    const mt = maille.material
    if (Array.isArray(mt)) for (const x of mt) x.dispose()
    else if (mt) mt.dispose()
  })
  m.materiaux.length = 0
}

function boite(l: number, h: number, p: number): THREE.BoxGeometry {
  return new THREE.BoxGeometry(l, h, p)
}

function cylindre(rayon: number, hauteur: number, segments: number): THREE.CylinderGeometry {
  return new THREE.CylinderGeometry(rayon, rayon, hauteur, segments)
}

function part(geo: THREE.BufferGeometry, couleur: number, transforme: THREE.Matrix4): Partie {
  return { geo, couleur, transforme }
}

function materiauCorps(): THREE.MeshLambertMaterial {
  return new THREE.MeshLambertMaterial({ vertexColors: true })
}

// -------------------------------------------------------------------- voiture

interface PaletteVoiture {
  carrosserie: number
  toit: number
  bande: number
  vitre: number
  pareChoc: number
  plaque: number
  feu: number
  roue: number
  jante: number
}

/**
 * Palette d'une voiture. La carrosserie porte la couleur du proprietaire: c'est
 * ce qui se lit de dessus a 45 degres. Le toit est creme, avec une bande de la
 * meme couleur, et les plaques la reprennent en clair sur les pare-chocs
 * sombres.
 */
function paletteVoiture(couleur: number): PaletteVoiture {
  return {
    carrosserie: melangerCouleur(couleur, 0x1c2026, 0.14),
    toit: 0xf2f4f6,
    bande: couleur,
    vitre: 0x8fc9e0,
    pareChoc: 0x353a42,
    plaque: melangerCouleur(couleur, 0xffffff, 0.35),
    feu: 0xe0402e,
    roue: 0x24272c,
    jante: 0xc3cad2,
  }
}

function construireVoiture(couleur: number): MaillageVehicule {
  const c = paletteVoiture(couleur)
  const parties: Partie[] = [
    // Caisse, capot et coffre.
    part(boite(1.86, 0.5, 3.9), c.carrosserie, tr([0, 0.67, 0])),
    part(boite(1.74, 0.26, 1.15), c.carrosserie, tr([0, 0.99, 1.32])),
    part(boite(1.74, 0.26, 0.85), c.carrosserie, tr([0, 0.99, -1.55])),
    /*
     * DECAPOTABLE. La voiture avait une cabine fermee et un toit a 1,5 m, et
     * le pilote — assis a 0,95 m — passait la tete et les epaules a travers
     * (vu au telephone le 13 octobre). Dans un monde partage, voir QUI conduit
     * compte: JC a choisi la decapotable. Un habitacle ouvert: flancs bas,
     * tableau de bord, banquette creme, pare-brise, et la capote repliee sur le
     * coffre, a la couleur du proprietaire — c'est elle qui reprend la bande
     * de l'ancien toit.
     */
    part(boite(0.12, 0.3, 1.85), c.carrosserie, tr([0.81, 1.07, -0.15])),
    part(boite(0.12, 0.3, 1.85), c.carrosserie, tr([-0.81, 1.07, -0.15])),
    part(boite(1.62, 0.3, 0.3), c.carrosserie, tr([0, 1.07, 0.62])),
    part(boite(1.62, 0.3, 0.14), c.carrosserie, tr([0, 1.07, -1.0])),
    part(boite(1.3, 0.14, 0.62), c.toit, tr([0, 0.99, -0.3])),
    part(boite(1.3, 0.44, 0.12), c.toit, tr([0, 1.2, -0.66], [-0.15, 0, 0])),
    part(boite(1.5, 0.2, 0.36), c.bande, tr([0, 1.19, -1.25])),
    // Pare-brise incline, pose sur le tableau de bord.
    part(boite(1.46, 0.42, 0.06), c.vitre, tr([0, 1.4, 0.74], [-0.42, 0, 0])),
    // Pare-chocs et plaques a la couleur du proprietaire.
    part(boite(1.8, 0.24, 0.22), c.pareChoc, tr([0, 0.56, 2.02])),
    part(boite(1.8, 0.24, 0.22), c.pareChoc, tr([0, 0.56, -2.02])),
    part(boite(0.62, 0.18, 0.06), c.plaque, tr([0, 0.56, 2.15])),
    part(boite(0.62, 0.18, 0.06), c.plaque, tr([0, 0.56, -2.15])),
    // Feux arriere et retroviseurs.
    part(boite(0.3, 0.14, 0.07), c.feu, tr([0.62, 0.82, -1.98])),
    part(boite(0.3, 0.14, 0.07), c.feu, tr([-0.62, 0.82, -1.98])),
    part(boite(0.16, 0.1, 0.08), c.carrosserie, tr([0.96, 1.24, 0.55])),
    part(boite(0.16, 0.1, 0.08), c.carrosserie, tr([-0.96, 1.24, 0.55])),
  ]

  const matCorps = materiauCorps()
  const corps = new THREE.Mesh(fusionner(parties), matCorps)
  corps.castShadow = true
  corps.receiveShadow = true

  const groupe = new THREE.Group()
  groupe.add(corps)

  // Un essieu = les deux roues du meme axe, fusionnees autour de leur centre
  // commun. Une seule rotation autour de x les fait rouler toutes les deux.
  const essieux: THREE.Mesh[] = []
  for (const z of [Z_ESSIEU_AVANT, Z_ESSIEU_ARRIERE]) {
    const roues: Partie[] = []
    for (const signe of [1, -1]) {
      const x = signe * X_ROUE
      roues.push(part(cylindre(RAYON_ROUE, 0.26, 12), c.roue, tr([x, 0, 0], [0, 0, Math.PI / 2])))
      roues.push(part(cylindre(0.15, 0.29, 8), c.jante, tr([x, 0, 0], [0, 0, Math.PI / 2])))
    }
    const essieu = new THREE.Mesh(fusionner(roues), matCorps)
    essieu.position.set(0, RAYON_ROUE, z)
    essieu.castShadow = true
    groupe.add(essieu)
    essieux.push(essieu)
  }

  const matPhares = new THREE.MeshLambertMaterial({ color: 0xfff3d0, emissive: PHARE_ETEINT })
  const phares = new THREE.Mesh(
    fusionner([
      part(boite(0.32, 0.18, 0.08), 0xffffff, tr([0.6, 0.8, 2.0])),
      part(boite(0.32, 0.18, 0.08), 0xffffff, tr([-0.6, 0.8, 2.0])),
    ]),
    matPhares,
  )
  groupe.add(phares)

  return {
    groupe,
    essieux,
    rames: [],
    phares,
    ancre: null,
    sillage: null,
    matSillage: null,
    matPhares,
    materiaux: [matCorps, matPhares],
  }
}

// --------------------------------------------------------------------- barque

interface PaletteBarque {
  coque: number
  plancher: number
  platBord: number
  banc: number
  rame: number
  metal: number
  corde: number
}

function paletteBarque(couleur: number): PaletteBarque {
  return {
    coque: 0xa9743c,
    plancher: 0xc79a63,
    platBord: couleur,
    banc: melangerCouleur(couleur, 0xffffff, 0.4),
    rame: 0xd8b47a,
    metal: 0x6c757e,
    corde: 0xd9cdb4,
  }
}

/**
 * Une rame, geometrie construite autour de son TOLET (l'origine), pour que le
 * maillage puisse pivoter dessus. `cote` vaut -1 a babord, +1 a tribord.
 */
function construireRame(cote: number, c: PaletteBarque, matCorps: THREE.Material): THREE.Mesh {
  const parties: Partie[] = [
    part(cylindre(0.042, 1.35, 6), c.rame, tr([cote * 0.6, 0, 0], [0, 0, Math.PI / 2])),
    part(boite(0.4, 0.2, 0.05), c.rame, tr([cote * 1.34, -0.03, 0])),
    // Poignee, du cote interieur de la barque.
    part(cylindre(0.055, 0.2, 6), c.metal, tr([-cote * 0.13, 0, 0], [0, 0, Math.PI / 2])),
  ]
  const rame = new THREE.Mesh(fusionner(parties), matCorps)
  rame.position.set(cote * 0.72, 0.42, 0.1)
  rame.castShadow = true
  return rame
}

/**
 * Plan de la coque, en metres. La partie droite s'arrete a l'epaule, puis deux
 * bordes convergent vers l'etrave: c'est ce V qui fait lire "barque" et non
 * "caisse" dans une vue de dessus a 45 degres. La premiere version avait une
 * proue en pyramide couchee, et de haut on ne voyait qu'un rectangle.
 */
const Z_POUPE = -1.52
const Z_EPAULE = 1.1
const Z_PROUE = 1.8
const X_BORDE = 0.66

function construireBarque(couleur: number): MaillageVehicule {
  const c = paletteBarque(couleur)
  const longueurDroite = Z_EPAULE - Z_POUPE
  const centreDroite = (Z_EPAULE + Z_POUPE) / 2
  const longueurProue = Math.hypot(X_BORDE, Z_PROUE - Z_EPAULE)
  const angleProue = Math.atan2(X_BORDE, Z_PROUE - Z_EPAULE)
  const parties: Partie[] = [
    // Fond, plancher clair, et un fond de proue plus etroit.
    part(boite(1.2, 0.16, longueurDroite), c.coque, tr([0, -0.1, centreDroite])),
    part(boite(1.05, 0.05, longueurDroite - 0.25), c.plancher, tr([0, 0.04, centreDroite])),
    part(boite(0.62, 0.16, 0.8), c.coque, tr([0, -0.09, 1.4])),
    // Bordes droits, inclines vers l'exterieur.
    part(boite(0.11, 0.44, longueurDroite), c.coque, tr([X_BORDE, 0.12, centreDroite], [0, 0, -0.14])),
    part(boite(0.11, 0.44, longueurDroite), c.coque, tr([-X_BORDE, 0.12, centreDroite], [0, 0, 0.14])),
    // Les deux bordes de proue et l'etrave: le V.
    part(boite(0.11, 0.44, longueurProue), c.coque, tr([X_BORDE / 2, 0.12, (Z_EPAULE + Z_PROUE) / 2], [0, -angleProue, 0])),
    part(boite(0.11, 0.44, longueurProue), c.coque, tr([-X_BORDE / 2, 0.12, (Z_EPAULE + Z_PROUE) / 2], [0, angleProue, 0])),
    part(boite(0.17, 0.52, 0.17), c.coque, tr([0, 0.16, Z_PROUE - 0.08])),
    // Tableau arriere.
    part(boite(1.15, 0.44, 0.12), c.coque, tr([0, 0.12, Z_POUPE])),
    // Plat-bord a la couleur du proprietaire: c'est le lisere qui se lit de haut.
    part(boite(0.15, 0.08, longueurDroite), c.platBord, tr([0.7, 0.35, centreDroite], [0, 0, -0.14])),
    part(boite(0.15, 0.08, longueurDroite), c.platBord, tr([-0.7, 0.35, centreDroite], [0, 0, 0.14])),
    part(boite(0.15, 0.08, longueurProue), c.platBord, tr([X_BORDE / 2, 0.35, (Z_EPAULE + Z_PROUE) / 2], [0, -angleProue, 0])),
    part(boite(0.15, 0.08, longueurProue), c.platBord, tr([-X_BORDE / 2, 0.35, (Z_EPAULE + Z_PROUE) / 2], [0, angleProue, 0])),
    part(boite(1.25, 0.08, 0.16), c.platBord, tr([0, 0.35, Z_POUPE + 0.02])),
    // Deux bancs et les tolets.
    part(boite(1.28, 0.09, 0.3), c.banc, tr([0, 0.22, 0.55])),
    part(boite(1.28, 0.09, 0.3), c.banc, tr([0, 0.22, -0.6])),
    part(boite(0.1, 0.14, 0.1), c.metal, tr([0.72, 0.4, 0.1])),
    part(boite(0.1, 0.14, 0.1), c.metal, tr([-0.72, 0.4, 0.1])),
  ]

  const matCorps = materiauCorps()
  const coque = new THREE.Mesh(fusionner(parties), matCorps)
  coque.castShadow = true
  coque.receiveShadow = true

  const groupe = new THREE.Group()
  groupe.add(coque)

  const rames = [-1, 1].map((cote) => {
    const maillage = construireRame(cote, c, matCorps)
    groupe.add(maillage)
    return { maillage, cote }
  })

  // Ancre: cordage puis jas et pattes, sous la proue. Cachee quand elle est
  // remontee — c'est le seul signe visible du mouillage.
  // Le cordage part du plat-bord et l'ancre reste juste sous la surface: plus
  // bas, l'eau (opaque a 78 %) l'avalait et on ne voyait plus si on mouillait.
  const ancre = new THREE.Mesh(
    fusionner([
      part(cylindre(0.025, 0.7, 5), c.corde, tr([0, 0.1, 1.34])),
      part(boite(0.09, 0.5, 0.09), c.metal, tr([0, -0.42, 1.34])),
      part(boite(0.5, 0.07, 0.07), c.metal, tr([0, -0.24, 1.34])),
      part(boite(0.18, 0.16, 0.06), c.metal, tr([0.2, -0.66, 1.34], [0, 0, 0.7])),
      part(boite(0.18, 0.16, 0.06), c.metal, tr([-0.2, -0.66, 1.34], [0, 0, -0.7])),
    ]),
    matCorps,
  )
  groupe.add(ancre)

  // Sillage: deux bras en V derriere la poupe et deux rides plus courtes. Plaque
  // a plat sur l'eau, sans ecriture de profondeur, comme l'ecume du rivage.
  const matSillage = new THREE.MeshBasicMaterial({
    color: 0xffffff,
    vertexColors: true,
    transparent: true,
    opacity: 0,
    depthWrite: false,
  })
  const bras: Partie[] = []
  for (const cote of [-1, 1]) {
    const plan = new THREE.PlaneGeometry(0.36, 3.0)
    plan.rotateX(-Math.PI / 2)
    bras.push(part(plan, 0xffffff, tr([cote * 0.8, 0, -2.7], [0, cote * 0.22, 0])))
    const ride = new THREE.PlaneGeometry(0.26, 1.1)
    ride.rotateX(-Math.PI / 2)
    bras.push(part(ride, 0xffffff, tr([cote * 0.5, 0, -1.85], [0, cote * 0.45, 0])))
  }
  const sillage = new THREE.Mesh(fusionner(bras), matSillage)
  sillage.renderOrder = 4
  groupe.add(sillage)

  return {
    groupe,
    essieux: [],
    rames,
    phares: null,
    ancre,
    sillage,
    matSillage,
    matPhares: null,
    materiaux: [matCorps, matSillage],
  }
}

// --------------------------------------------------------------------- avions

/**
 * LES TROIS APPAREILS. Ils volent EXACTEMENT pareil (shared/sim/avion.ts ne
 * connait aucun modele): ce qui suit ne change que la silhouette, et c'est tout
 * ce que « different models but functionally the same » demande.
 *
 * La couleur du proprietaire est sur le FUSELAGE, comme la carrosserie d'une
 * voiture: c'est ce qu'on lit de dessus a 45 degres. Le reste — ailes claires,
 * verriere bleutee, helice sombre — est commun aux trois, pour qu'on reconnaisse
 * un avion avant de reconnaitre lequel.
 *
 * Le repere est celui de tous les vehicules: +z vers l'avant, y = 0 au sol.
 */
interface PaletteAvion {
  fuselage: number
  aile: number
  derive: number
  vitre: number
  metal: number
  pneu: number
}

function paletteAvion(couleur: number): PaletteAvion {
  return {
    fuselage: melangerCouleur(couleur, 0xffffff, 0.12),
    aile: melangerCouleur(couleur, 0xf4f6f8, 0.55),
    derive: couleur,
    vitre: 0x8fc9e0,
    metal: 0x8c949c,
    pneu: 0x24272c,
  }
}

/** Le train d'atterrissage, commun aux trois: deux roues et une roulette. */
function trainAvion(c: PaletteAvion, hauteur: number): Partie[] {
  const parties: Partie[] = []
  for (const cote of [-1, 1]) {
    parties.push(part(cylindre(0.04, hauteur, 6), c.metal, tr([cote * 0.85, hauteur / 2, 0.2])))
    parties.push(
      part(cylindre(0.24, 0.16, 10), c.pneu, tr([cote * 0.85, 0.24, 0.2], [0, 0, Math.PI / 2])),
    )
  }
  parties.push(part(cylindre(0.035, hauteur * 0.8, 6), c.metal, tr([0, hauteur * 0.4, -2.4])))
  parties.push(part(cylindre(0.16, 0.12, 8), c.pneu, tr([0, 0.16, -2.4], [0, 0, Math.PI / 2])))
  return parties
}

/**
 * Assemble un appareil: le corps fusionne, l'helice a part (elle tourne), et le
 * groupe qui porte le tout.
 */
function monterAvion(parties: Partie[], helice: Partie[] | null, posHelice: [number, number, number]): MaillageVehicule {
  const matCorps = materiauCorps()
  const corps = new THREE.Mesh(fusionner(parties), matCorps)
  corps.castShadow = true
  corps.receiveShadow = true
  const groupe = new THREE.Group()
  groupe.add(corps)

  // L'HELICE EST UN ESSIEU. Elle tourne autour de z et non de x, mais le
  // mecanisme des roues fait exactement ce qu'il faut — un maillage a part,
  // pivote a chaque image selon la vitesse. On la range donc dans `essieux`
  // plutot que d'inventer une seconde liste et une seconde boucle.
  const essieux: THREE.Mesh[] = []
  if (helice) {
    const maille = new THREE.Mesh(fusionner(helice), matCorps)
    maille.position.set(...posHelice)
    maille.castShadow = true
    groupe.add(maille)
    essieux.push(maille)
  }

  const matPhares = new THREE.MeshLambertMaterial({ color: 0xfff3d0, emissive: PHARE_ETEINT })
  const phares = new THREE.Mesh(
    fusionner([
      part(boite(0.18, 0.12, 0.08), 0xffffff, tr([1.9, 0.75, 0.9])),
      part(boite(0.18, 0.12, 0.08), 0xffffff, tr([-1.9, 0.75, 0.9])),
    ]),
    matPhares,
  )
  groupe.add(phares)

  return {
    groupe,
    essieux,
    rames: [],
    phares,
    ancre: null,
    sillage: null,
    matSillage: null,
    matPhares,
    materiaux: [matCorps, matPhares],
  }
}

/** L'avion de tourisme: aile haute, helice, une verriere ronde. */
function construireAvion(couleur: number): MaillageVehicule {
  const c = paletteAvion(couleur)
  const parties: Partie[] = [
    // Fuselage: un corps, un nez, une queue effilee.
    part(boite(0.9, 0.95, 3.9), c.fuselage, tr([0, 1.25, 0.2])),
    part(cylindre(0.42, 0.7, 10), c.fuselage, tr([0, 1.25, 2.3], [Math.PI / 2, 0, 0])),
    part(boite(0.55, 0.6, 1.6), c.fuselage, tr([0, 1.35, -2.0])),
    // Verriere.
    part(boite(0.78, 0.42, 1.5), c.vitre, tr([0, 1.72, 0.75])),
    // AILE HAUTE, d'un seul tenant: c'est elle qu'on voit d'en haut, et c'est
    // ce qui donne son envergure a l'appareil.
    part(boite(6.6, 0.14, 1.25), c.aile, tr([0, 1.95, 0.35])),
    part(boite(0.14, 0.5, 0.5), c.metal, tr([0.9, 1.7, 0.35])),
    part(boite(0.14, 0.5, 0.5), c.metal, tr([-0.9, 1.7, 0.35])),
    // Empennage: plan horizontal et derive.
    part(boite(2.3, 0.1, 0.65), c.aile, tr([0, 1.5, -2.6])),
    part(boite(0.1, 1.0, 0.9), c.derive, tr([0, 1.95, -2.65])),
    // Le cone d'helice.
    part(cylindre(0.16, 0.3, 8), c.metal, tr([0, 1.25, 2.62], [Math.PI / 2, 0, 0])),
    ...trainAvion(c, 0.5),
  ]
  const helice: Partie[] = [
    part(boite(0.12, 2.0, 0.05), 0x2b2f34, tr([0, 0, 0])),
    part(boite(2.0, 0.12, 0.05), 0x2b2f34, tr([0, 0, 0])),
  ]
  return monterAvion(parties, helice, [0, 1.25, 2.78])
}

/** Le biplan: deux ailes, des haubans, un gros moteur en etoile. */
function construireBiplan(couleur: number): MaillageVehicule {
  const c = paletteAvion(couleur)
  const parties: Partie[] = [
    part(boite(0.85, 0.9, 3.6), c.fuselage, tr([0, 1.15, 0.1])),
    part(cylindre(0.46, 0.6, 10), c.metal, tr([0, 1.15, 2.1], [Math.PI / 2, 0, 0])),
    part(boite(0.5, 0.55, 1.5), c.fuselage, tr([0, 1.2, -2.1])),
    // Deux places a l'air libre, comme un vrai biplan.
    part(boite(0.62, 0.3, 0.6), 0x2b2f34, tr([0, 1.62, 0.95])),
    part(boite(0.62, 0.3, 0.6), 0x2b2f34, tr([0, 1.62, 0.05])),
    // AILE BASSE ET AILE HAUTE, decalees: c'est la silhouette du biplan.
    part(boite(6.4, 0.13, 1.15), c.aile, tr([0, 0.95, 0.5])),
    part(boite(6.8, 0.13, 1.2), c.aile, tr([0, 2.05, 0.25])),
    // Les mats et les haubans entre les deux ailes.
    ...[-2.4, -1.2, 1.2, 2.4].map((x) => part(boite(0.09, 1.1, 0.5), c.metal, tr([x, 1.5, 0.35]))),
    ...[-1.8, 1.8].map((x) => part(boite(0.05, 1.5, 0.05), c.metal, tr([x, 1.5, 0.35], [0.35, 0, 0]))),
    // Empennage.
    part(boite(2.1, 0.1, 0.6), c.aile, tr([0, 1.25, -2.7])),
    part(boite(0.1, 0.95, 0.85), c.derive, tr([0, 1.7, -2.75])),
    part(cylindre(0.14, 0.26, 8), c.metal, tr([0, 1.15, 2.4], [Math.PI / 2, 0, 0])),
    ...trainAvion(c, 0.65),
  ]
  const helice: Partie[] = [
    part(boite(0.14, 2.2, 0.05), 0x1f2226, tr([0, 0, 0])),
    part(boite(2.2, 0.14, 0.05), 0x1f2226, tr([0, 0, 0])),
  ]
  return monterAvion(parties, helice, [0, 1.15, 2.54])
}

/** Le jet: ailes en fleche, deux reacteurs, pas d'helice. */
function construireJet(couleur: number): MaillageVehicule {
  const c = paletteAvion(couleur)
  const parties: Partie[] = [
    part(boite(1.0, 1.0, 4.4), c.fuselage, tr([0, 1.35, 0])),
    part(cylindre(0.5, 1.1, 10), c.fuselage, tr([0, 1.35, 2.5], [Math.PI / 2, 0, 0])),
    part(cylindre(0.3, 0.7, 8), c.fuselage, tr([0, 1.4, 3.0], [Math.PI / 2, 0, 0])),
    // Verriere basse et longue: un jet se lit a son profil pointu.
    part(boite(0.82, 0.34, 1.9), c.vitre, tr([0, 1.82, 1.1])),
    // AILES EN FLECHE: deux panneaux tournes, plutot qu'une planche droite.
    part(boite(3.4, 0.13, 1.5), c.aile, tr([1.9, 1.15, -0.35], [0, -0.38, 0])),
    part(boite(3.4, 0.13, 1.5), c.aile, tr([-1.9, 1.15, -0.35], [0, 0.38, 0])),
    // Reacteurs sous les ailes.
    ...[-1.55, 1.55].map((x) => part(cylindre(0.32, 1.5, 10), c.metal, tr([x, 0.95, -0.4], [Math.PI / 2, 0, 0]))),
    ...[-1.55, 1.55].map((x) => part(cylindre(0.26, 0.2, 10), 0x1f2226, tr([x, 0.95, -1.14], [Math.PI / 2, 0, 0]))),
    // Empennage en T.
    part(boite(0.12, 1.35, 1.15), c.derive, tr([0, 2.1, -2.4])),
    part(boite(2.2, 0.1, 0.6), c.aile, tr([0, 2.72, -2.55])),
    ...trainAvion(c, 0.55),
  ]
  return monterAvion(parties, null, [0, 0, 0])
}

function construireMaillage(kind: VehiculeKind, couleur: number): MaillageVehicule {
  const m =
    kind === 'voiture'
      ? construireVoiture(couleur)
      : kind === 'barque'
        ? construireBarque(couleur)
        : kind === 'jet'
          ? construireJet(couleur)
          : kind === 'biplan'
            ? construireBiplan(couleur)
            : construireAvion(couleur)
  m.groupe.name = `vehicule:${kind}`
  return m
}

// ---------------------------------------------------------------------- classe

interface Entree {
  vehicule: Vehicule
  maillage: MaillageVehicule
  /** Position a l'image precedente, pour mesurer la vitesse. */
  precedenteX: number
  precedenteZ: number
  /** Vitesse lissee, en m/s. */
  vitesse: number
  /** Angle de rotation des roues — et de l'helice d'un avion, en radians. */
  angleRoue: number
  /** Phase du coup de rame et du roulis, pour desynchroniser les barques. */
  phase: number
  /** Cap a l'image precedente: c'est lui qui donne l'inclinaison d'un avion. */
  precedentRot: number
  /** Inclinaison courante d'un avion en virage, lissee. */
  roulis: number
  /**
   * L'ETAT AU DEBUT DU DERNIER TICK, pour un appareil en vol. `null` tant
   * qu'aucun tick n'a ete note (tout ce qui roule ou flotte).
   */
  avantTick: Pose | null
  /** La pose REELLEMENT dessinee cette image: interpolee pour un avion. */
  affichee: Pose
}

/** Une pose de vehicule: c'est tout ce que le rendu a besoin de connaitre. */
export interface Pose {
  x: number
  y: number
  z: number
  rot: number
}

export class RenduVehicules {
  readonly groupe: THREE.Group
  private scene: THREE.Scene
  private entrees = new Map<string, Entree>()
  private fantomeMaillage: MaillageVehicule | null = null
  private fantomeSignature = ''
  private facteurNuit = -1
  /** Liste des vehicules, mise en cache comme celle des batiments. */
  private cacheListe: Vehicule[] | null = null

  constructor(scene: THREE.Scene) {
    this.scene = scene
    this.groupe = new THREE.Group()
    this.groupe.name = 'vehicules'
    scene.add(this.groupe)
  }

  ajouter(v: Vehicule): void {
    if (this.entrees.has(v.id)) this.retirer(v.id)
    const maillage = construireMaillage(v.kind, v.couleur)
    maillage.groupe.position.set(v.pos.x, v.y, v.pos.z)
    maillage.groupe.rotation.y = v.rot
    if (maillage.ancre) maillage.ancre.visible = v.ancre
    this.groupe.add(maillage.groupe)
    this.cacheListe = null
    // Phase tiree de l'identifiant: deux barques ne ramment pas au meme rythme,
    // et la valeur ne bouge pas d'une image a l'autre.
    let h = 0
    for (let i = 0; i < v.id.length; i++) h = (h * 31 + v.id.charCodeAt(i)) % 6283
    this.entrees.set(v.id, {
      vehicule: v,
      maillage,
      precedenteX: v.pos.x,
      precedenteZ: v.pos.z,
      vitesse: 0,
      angleRoue: 0,
      phase: h / 1000,
      precedentRot: v.rot,
      roulis: 0,
      avantTick: null,
      affichee: { x: v.pos.x, y: v.y, z: v.pos.z, rot: v.rot },
    })
    if (this.facteurNuit >= 0) this.appliquerNuit(maillage, this.facteurNuit)
    journal.info('vehicule', `pose ${v.kind} en (${v.pos.x.toFixed(1)}, ${v.pos.z.toFixed(1)})`, { id: v.id })
  }

  /**
   * Met a jour ce qui CHANGE sans reconstruire le maillage: l'ancre et le
   * verrou.
   *
   * Repose entierement le vehicule serait plus court a ecrire, et faux: la
   * position qui fait foi est celle du pilote, pas celle de la base. Rejouer
   * `ajouter` sur une barque qu'on est en train de mener la teleporterait a son
   * dernier point sauvegarde a chaque fois que quelqu'un tourne une clef.
   *
   * Rend le vehicule tel qu'il est desormais connu, pour que l'appelant n'ait
   * pas a le rechercher.
   */
  majEtat(v: Vehicule): Vehicule | null {
    const e = this.entrees.get(v.id)
    if (!e) return null
    e.vehicule.ancre = v.ancre
    e.vehicule.verrouille = v.verrouille
    if (e.maillage.ancre) e.maillage.ancre.visible = v.ancre
    return e.vehicule
  }

  retirer(id: string): void {
    const e = this.entrees.get(id)
    if (!e) return
    this.groupe.remove(e.maillage.groupe)
    libererMaillage(e.maillage)
    this.entrees.delete(id)
    this.cacheListe = null
    journal.info('vehicule', `retire ${id}`)
  }

  /** La liste des vehicules poses. Mise en cache: elle est lue a chaque tick. */
  vehicules(): Vehicule[] {
    if (!this.cacheListe) this.cacheListe = [...this.entrees.values()].map((e) => e.vehicule)
    return this.cacheListe
  }

  nombreVehicules(): number {
    return this.entrees.size
  }

  /**
   * L'ETAT D'UN VEHICULE JUSTE AVANT UN PAS DE SIMULATION.
   *
   * A appeler dans la boucle de tick, avant `majAvion`. C'est la moitie « d'ou
   * l'on vient » de l'interpolation de rendu (voir `maj`): sans elle, un avion
   * avance par bonds d'un metre vingt fois par seconde pendant que l'ecran se
   * rafraichit soixante fois.
   */
  noterTick(v: Vehicule): void {
    const e = this.entrees.get(v.id)
    if (!e) return
    if (e.avantTick) {
      e.avantTick.x = v.pos.x
      e.avantTick.y = v.y
      e.avantTick.z = v.pos.z
      e.avantTick.rot = v.rot
    } else {
      e.avantTick = { x: v.pos.x, y: v.y, z: v.pos.z, rot: v.rot }
    }
  }

  /** La pose REELLEMENT dessinee, celle a laquelle il faut asseoir le pilote. */
  poseAffichee(id: string): Pose | null {
    return this.entrees.get(id)?.affichee ?? null
  }

  /**
   * Suit les vehicules et anime les parties mobiles.
   *
   * La vitesse n'est pas fournie par la simulation: on la mesure sur le
   * deplacement reel entre deux images, puis on la lisse. Le rendu reste donc
   * juste que le vehicule soit pilote, a la derive ou immobile.
   *
   * `alpha` EST CE QUI EMPECHE L'AVION DE TREMBLER. La simulation avance par
   * pas fixes de 50 ms — c'est ce qui la rend reproductible, et on n'y touche
   * pas — mais l'ecran, lui, affiche 60 images par seconde: une image sur
   * trois voit l'appareil sauter d'un metre entier (vingt metres par seconde),
   * les deux autres le voient immobile, pendant que la camera, elle, glisse.
   * A pied le pas fait dix-sept centimetres et personne ne le remarque; en
   * avion c'est une secousse a 20 Hz. On dessine donc l'appareil ENTRE ses deux
   * derniers etats de simulation, `alpha` etant la fraction de tick deja
   * ecoulee. Cela coute un tick de retard a l'affichage, et rien d'autre: la
   * simulation, elle, n'est pas touchee.
   */
  maj(dt: number, tempsSec: number, alpha = 1): void {
    const a = clamp(alpha, 0, 1)
    for (const e of this.entrees.values()) {
      const v = e.vehicule
      const m = e.maillage
      const vole = vehiculeDef(v.kind).milieu === 'air'
      const p = e.affichee
      if (vole && e.avantTick) {
        p.x = e.avantTick.x + (v.pos.x - e.avantTick.x) * a
        p.z = e.avantTick.z + (v.pos.z - e.avantTick.z) * a
        p.y = e.avantTick.y + (v.y - e.avantTick.y) * a
        // Le cap se prend par le plus court chemin: sans ca, un passage par
        // ±PI ferait faire un tour complet a l'appareil en une image.
        let dRot = v.rot - e.avantTick.rot
        if (dRot > Math.PI) dRot -= Math.PI * 2
        if (dRot < -Math.PI) dRot += Math.PI * 2
        p.rot = e.avantTick.rot + dRot * a
      } else {
        p.x = v.pos.x
        p.y = v.y
        p.z = v.pos.z
        p.rot = v.rot
      }
      const dx = p.x - e.precedenteX
      const dz = p.z - e.precedenteZ
      e.precedenteX = p.x
      e.precedenteZ = p.z
      const instant = dt > 1e-5 ? Math.hypot(dx, dz) / dt : 0
      const k = dt > 0 ? clamp(dt / LISSAGE_VITESSE, 0, 1) : 1
      e.vitesse += (instant - e.vitesse) * k

      const flotte = vehiculeDef(v.kind).milieu === 'eau'
      const tangage = flotte ? Math.sin(tempsSec * 1.3 + e.phase) * 0.045 : 0
      m.groupe.position.set(p.x, p.y + tangage, p.z)
      m.groupe.rotation.y = p.rot
      if (flotte) m.groupe.rotation.z = Math.sin(tempsSec * 0.9 + e.phase) * 0.03

      /*
       * UN AVION S'INCLINE DANS SES VIRAGES, et c'est ce qui fait qu'on le lit
       * comme un avion plutot que comme une maquette qui glisse. L'angle vient
       * de la vitesse de rotation REELLE de l'appareil (celle que la simulation
       * vient de produire), lissee: rien n'est invente ici, on ne fait que
       * montrer ce qui se passe deja.
       */
      if (vole) {
        let dRot = p.rot - e.precedentRot
        if (dRot > Math.PI) dRot -= Math.PI * 2
        if (dRot < -Math.PI) dRot += Math.PI * 2
        e.precedentRot = p.rot
        const vise = dt > 1e-5 ? clamp((dRot / dt) * 0.55, -0.7, 0.7) : 0
        e.roulis += (vise - e.roulis) * clamp(dt / 0.25, 0, 1)
        m.groupe.rotation.z = e.roulis
      }

      // Roues: elles roulent a la vraie vitesse du vehicule. Sur un avion, le
      // meme mecanisme fait tourner l'HELICE, autour de son axe a elle.
      if (m.essieux.length > 0) {
        if (vole) {
          // Le moteur tourne meme a l'arret au parking: un avion dont l'helice
          // est figee a l'air en panne.
          e.angleRoue += (7 + e.vitesse * 1.5) * dt
          for (const helice of m.essieux) helice.rotation.z = e.angleRoue
        } else {
          e.angleRoue += (e.vitesse / RAYON_ROUE) * dt
          for (const essieu of m.essieux) essieu.rotation.x = e.angleRoue
        }
      }

      // Rames: elles balayent quand la barque avance, et se relevent au retour.
      // Au repos elles ne sont PAS perpendiculaires a la coque: elles trainent
      // vers l'arriere, pelle dans l'eau. Bien droites, vues de 45 degres, les
      // deux rames s'alignaient en une seule tige qui traversait la barque.
      if (m.rames.length > 0) {
        const effort = clamp(e.vitesse / 1.5, 0, 1)
        const phase = tempsSec * 2.6 + e.phase
        const balayage = Math.sin(phase) * AMPLITUDE_RAME * effort - REPOS_RAME
        const leve = Math.cos(phase) * 0.22 * effort - PLONGEE_RAME
        for (const r of m.rames) {
          r.maillage.rotation.y = -r.cote * balayage
          r.maillage.rotation.z = r.cote * leve
        }
      }

      if (m.ancre) m.ancre.visible = v.ancre

      if (m.sillage && m.matSillage) {
        const force = clamp(e.vitesse / VITESSE_SILLAGE, 0, 1)
        m.matSillage.opacity = force * (0.3 + 0.1 * Math.sin(tempsSec * 5 + e.phase))
        m.sillage.visible = m.matSillage.opacity > 0.01
        // Le sillage reste a plat sur l'eau: on lui retire le tangage de la coque.
        m.sillage.position.y = 0.04 - tangage
      }
    }
  }

  /** Allume les phares la nuit. 0 = plein jour, 1 = pleine nuit. */
  majNuit(facteurNuit: number): void {
    const f = clamp(facteurNuit, 0, 1)
    if (this.facteurNuit >= 0 && Math.abs(f - this.facteurNuit) < 0.01) return
    this.facteurNuit = f
    for (const e of this.entrees.values()) this.appliquerNuit(e.maillage, f)
  }

  private appliquerNuit(m: MaillageVehicule, facteurNuit: number): void {
    if (!m.matPhares) return
    const t = smoothstep(0.25, 0.7, facteurNuit)
    m.matPhares.emissive.setHex(melangerCouleur(PHARE_ETEINT, PHARE_ALLUME, t))
  }

  // ------------------------------------------------------------- fantome

  /**
   * Apercu translucide avant de poser, vert si le placement est valide, rouge
   * sinon. Le maillage n'est reconstruit que si le modele ou la couleur change:
   * la rotation est libre, donc la tourner ne coute qu'une affectation.
   */
  fantome(kind: VehiculeKind, pos: Vec2, y: number, rot: number, valide: boolean, couleur: number): void {
    const signature = `${kind}:${couleur}`
    if (signature !== this.fantomeSignature) {
      this.cacherFantome()
      const m = construireMaillage(kind, couleur)
      m.groupe.traverse((o) => {
        const maille = o as THREE.Mesh
        if (!maille.isMesh) return
        maille.castShadow = false
        maille.receiveShadow = false
      })
      // Un fantome ne mouille pas et ne laisse pas de sillage.
      if (m.ancre) m.ancre.visible = false
      if (m.sillage) m.sillage.visible = false
      this.fantomeMaillage = m
      this.fantomeSignature = signature
      this.groupe.add(m.groupe)
    }
    const m = this.fantomeMaillage
    if (!m) return
    m.groupe.position.set(pos.x, y + 0.02, pos.z)
    m.groupe.rotation.y = rot
    m.groupe.rotation.z = 0
    m.groupe.visible = true

    const teinte = valide ? 0x63c76a : 0xef5f5f
    for (const mt of m.materiaux) {
      if (mt === m.matSillage) continue
      const lam = mt as THREE.MeshLambertMaterial
      lam.transparent = true
      lam.opacity = 0.55
      lam.depthWrite = false
      if (lam.emissive) lam.emissive.setHex(teinte)
    }
  }

  cacherFantome(): void {
    if (!this.fantomeMaillage) return
    this.groupe.remove(this.fantomeMaillage.groupe)
    libererMaillage(this.fantomeMaillage)
    this.fantomeMaillage = null
    this.fantomeSignature = ''
  }

  dispose(): void {
    for (const id of [...this.entrees.keys()]) this.retirer(id)
    this.cacherFantome()
    this.scene.remove(this.groupe)
  }
}
